function sleep(ms) {
  return new Promise(res => setTimeout(res, ms));
}

/**
 * Fetch all files in a Commons category with maxlag detection,
 * exponential backoff, and post-50k throttling.
 *
 * @param {string} category - Category title (e.g. "Category:Foo").
 * @returns {Promise<Array>} List of file titles.
 * 
 * 
 Example of response :
 ```
 {
    "batchcomplete": true,
    "continue": {
        "cmcontinue": "file|5241530a4c4c2d513135302028465241292d5955472d5241532e574156|155108072",
        "continue": "-||"
    },
    "query": {
        "categorymembers": [
            {
                "pageid": 155108090,
                "ns": 6,
                "title": "File:LL-Q150 (fra)-Yug-88.wav"
            },
            {
                "pageid": 81376347,
                "ns": 6,
                "title": "File:LL-Q33287 (gaa)-Justice Okai-allotey (Yug)-a.wav"
            },
        ]
    }
}
```
 * 
 */
async function fetchCategoryFiles(category) {
  const results = [];
  let cont = null;
  let totalFetched = 0;

  const API = "https://commons.wikimedia.org/w/api.php";

  // Exponential backoff settings
  let backoff = 500;            // initial backoff in ms
  const backoffMax = 10000;     // cap
  const backoffFactor = 1.8;    // exponential growth

  while (true) {
    const params = new URLSearchParams({
      action: "query",
      list: "categorymembers",
      cmtitle: category,
      cmtype: "file",
      cmlimit: "500",
      format: "json",
      origin: "*"
    });

    if (cont) params.set("cmcontinue", cont);

    const url = `${API}?${params.toString()}`;

    let data = null;

    // --- TRY REQUEST WITH RETRY LOGIC ---
    try {
      const res = await fetch(url);

      // Handle HTTP rate limits
      if (res.status === 429) {
        console.warn("￼ 429 Too Many Requests — backing off:", backoff, "ms");
        await sleep(backoff);
        backoff = Math.min(backoff * backoffFactor, backoffMax);
        continue; // retry
      }

      data = await res.json();

      // Detect maxlag errors (MediaWiki backend overload)
      if (data.error && data.error.code === "maxlag") {
        console.warn("￼ maxlag detected — waiting:", backoff, "ms");
        await sleep(backoff);
        backoff = Math.min(backoff * backoffFactor, backoffMax);
        continue; // retry
      }

      // If no error: reset backoff to be snappy again
      backoff = 500;

    } catch (err) {
      console.error("￼ Network error — retrying:", err);
      await sleep(backoff);
      backoff = Math.min(backoff * backoffFactor, backoffMax);
      continue; // retry
    }

    // ----- PROCESS RESULTS -----
    const batch = data.query.categorymembers;
    totalFetched += batch.length;

    // Extract titles from pages
    const titles = batch.map(item => item.title);
    results.push(...titles);

    // Get continuation or stop
    cont = data.continue?.cmcontinue;
    if (!cont) break;

    // ---- POST-50K THROTTLING ----
    if (totalFetched > 50000) {
      await sleep(150);  // Gentle throttling
    }
  }

  return results;
}

/**
 * Filter files by filename substring.
 *
 * @param {Array} files - Array of file titles to filter.
 * @param {string} matchSubstring - Substring to filter (e.g. "Merlin").
 * @returns {Array} List of matching file titles.
 */
function filterCategoryFiles(files, matchSubstring) {
  return files.filter(title => title.includes(matchSubstring));
}

/**
 * Fetch all files in a Commons category and filter by filename substring,
 * with maxlag detection, exponential backoff, and post-50k throttling.
 *
 * @param {string} category - Category title (e.g. "Category:Foo").
 * @param {string} matchSubstring - Substring to filter (e.g. "Merlin").
 * @returns {Promise<Array} List of matching file objects.
 */
async function fetchCategoryFilesFiltered(category, matchSubstring) {
  const allFiles = await fetchCategoryFiles(category);
  return filterCategoryFiles(allFiles, matchSubstring);
}

export { sleep, fetchCategoryFiles, filterCategoryFiles, fetchCategoryFilesFiltered };
